/**
 * Copy plain text to the clipboard, falling back from writeText to a ClipboardItem write. Resolves
 * false when the Clipboard API is missing (an insecure context) or refuses both.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fall through to the ClipboardItem write
  }
  try {
    await navigator.clipboard.write([new ClipboardItem({ "text/plain": new Blob([text], { type: "text/plain" }) })]);
    return true;
  } catch {
    return false;
  }
}

const BLOCK_TAGS = new Set(["p", "div", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "pre", "ul", "ol"]);

const isBlockNode = (node: Node): boolean =>
  node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as HTMLElement).tagName.toLowerCase());

/**
 * Renders an element's HTML as the plain-text half of a clipboard copy: `<br>` becomes a
 * newline, list items become bullets, and block siblings — nested ones included, a list
 * item's children among them — are separated by a blank line so paragraphs don't run
 * together in a paste target that only takes plain text.
 *
 * Works on a copy of the element, so the caller's DOM is left alone.
 */
function htmlToPlainText(source: HTMLElement): string {
  const root = source.cloneNode(true) as HTMLElement;

  // Replace <br> tags with newlines
  const brElements = root.querySelectorAll("br");
  brElements.forEach((br) => {
    br.replaceWith(document.createTextNode("\n"));
  });

  // Function to extract text from a node, preserving list structure
  const extractTextFromNode = (node: Node, depth = 0): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return "";
    }

    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    // Handle lists - extract all list items with proper formatting
    if (tagName === "ul" || tagName === "ol") {
      const items = Array.from(element.children)
        .filter((el) => el.tagName.toLowerCase() === "li")
        .map((li) => {
          const listText = concatChildren(li as HTMLElement, depth).trim();
          const bullet = tagName === "ol" ? "" : "• ";
          return `${bullet}${listText}`;
        })
        .filter((text) => text.trim().length > 0);
      return items.join("\n");
    }

    // Handle list items - but only if not inside a ul/ol (shouldn't happen, but safe)
    if (tagName === "li") {
      return `• ${concatChildren(element, depth).trim()}`;
    }

    // Handle block elements
    if (BLOCK_TAGS.has(tagName)) {
      return concatChildren(element, depth).trim();
    }

    // For other elements, recursively extract text
    return concatChildren(element, depth);
  };

  // Inline children concatenate verbatim to preserve intra-sentence spacing; block children are
  // separated by a blank line, and whitespace-only text nodes between tags are dropped.
  const concatChildren = (parent: HTMLElement, depth: number): string => {
    let result = "";
    for (const child of Array.from(parent.childNodes)) {
      const text = extractTextFromNode(child, depth + 1);
      if (isBlockNode(child)) {
        if (!text.trim()) continue;
        result = result.trimEnd();
        if (result.length > 0) result += "\n\n";
      }
      result += text;
    }
    return result;
  };

  // Extract text from all top-level children
  const textParts = Array.from(root.children)
    .map((child) => {
      return extractTextFromNode(child);
    })
    .filter((text) => text.trim().length > 0);

  const plainText = textParts.length > 0 ? textParts.join("\n\n") : root.textContent || "";

  return plainText.trim();
}

/**
 * Cleans HTML content by removing citations and interactive elements
 * for cleaner copying to clipboard.
 *
 * @param element - The HTML element to clean
 * @returns Object containing cleaned HTML and plain text
 */
export function cleanContentForCopy(element: HTMLElement): {
  html: string;
  plainText: string;
} {
  const clonedContent = element.cloneNode(true) as HTMLElement;

  const citationElements = clonedContent.querySelectorAll("[data-citation], cite, [role='citation']");
  citationElements.forEach((el) => el.remove());

  const buttonElements = clonedContent.querySelectorAll("button");
  buttonElements.forEach((el) => el.remove());

  // <br> is left standing: htmlToPlainText turns it into a newline on its own clone, and the
  // html below is what a rich paste target reads, where a bare newline collapses to a space.
  return {
    html: clonedContent.innerHTML,
    plainText: htmlToPlainText(clonedContent),
  };
}

/**
 * Copies rich text content to clipboard, preserving formatting but removing citations.
 *
 * @param element - The HTML element to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyRichTextToClipboard(element: HTMLElement): Promise<boolean> {
  try {
    const { html, plainText } = cleanContentForCopy(element);

    await navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([plainText], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" }),
      }),
    ]);

    return true;
  } catch {
    return false;
  }
}

/**
 * Re-encodes an image to PNG, the one image type every browser accepts on the
 * clipboard. A transcript image may have been pasted in as JPEG, WebP, or GIF.
 */
async function pngFrom(url: string): Promise<Blob> {
  // fetch resolves on a 4xx, and these URLs expire — without this the 403 body
  // is handed to createImageBitmap, which fails with an opaque decode error.
  const res = await fetch(url);
  if (!res.ok) throw new Error(`fetching the image failed: ${res.status}`);
  const blob = await res.blob();
  if (blob.type === "image/png") return blob;

  const bitmap = await createImageBitmap(blob);
  const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("no 2d context to re-encode the image");
  context.drawImage(bitmap, 0, 0);
  return canvas.convertToBlob({ type: "image/png" });
}

/**
 * Copies an image to the clipboard as a picture, so it pastes into a document
 * rather than as a link. The blob is handed over as a promise: Safari requires
 * the write call itself to happen inside the click, before any await.
 */
export async function copyImageToClipboard(url: string): Promise<boolean> {
  try {
    await navigator.clipboard.write([new ClipboardItem({ "image/png": pngFrom(url) })]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Copies pre-rendered rich text to the clipboard as both HTML and a plain-text
 * fallback, so paste targets that don't accept HTML still receive readable text.
 *
 * @param html - The HTML representation to place on the clipboard
 * @param plainText - The plain-text fallback
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyHtmlToClipboard(html: string, plainText: string): Promise<boolean> {
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        "text/plain": new Blob([plainText], { type: "text/plain" }),
        "text/html": new Blob([html], { type: "text/html" }),
      }),
    ]);
    return true;
  } catch {
    return false;
  }
}

/**
 * Removes citation tags from markdown text.
 *
 * @param markdown - The markdown text containing citations
 * @returns Markdown text with citations removed
 */
export function stripCitationsFromMarkdown(markdown: string): string {
  return (
    markdown
      // Remove <cite>...</cite> tags
      .replace(/<cite>.*?<\/cite>/g, "")
      // Remove raw KB citation patterns: asset_{id}_cxp_{position}
      .replace(/asset_[a-zA-Z0-9]+_cxp_\d+/g, "")
      // Remove raw simple asset citations: asset_{id}
      .replace(/asset_[a-zA-Z0-9]+/g, "")
      // Remove raw web citation patterns: web_toolexecution_{id}_{index}
      .replace(/web_toolexecution_[a-zA-Z0-9]+_\d+/g, "")
  );
}

/**
 * Copies markdown content to clipboard, removing citations.
 *
 * @param markdown - The markdown text to copy
 * @returns Promise that resolves to true if successful, false otherwise
 */
export async function copyMarkdownToClipboard(markdown: string): Promise<boolean> {
  try {
    const cleanedMarkdown = stripCitationsFromMarkdown(markdown);
    await navigator.clipboard.writeText(cleanedMarkdown);
    return true;
  } catch {
    return false;
  }
}
